import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";

/**
 * Custom error class for invalid phone numbers.
 */
class InvalidPhoneNumberError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = "InvalidPhoneNumberError";
  }
}

/**
 * Custom error class for phone number formatting errors.
 */
class PhoneNumberFormatError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = "PhoneNumberFormatError";
  }
}

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Validates a phone number.
 * @param {string} phone - The phone number to validate.
 * @param {string} countryCode - The ISO 3166-1 two-letter country code.
 * @returns {boolean} - Returns true if the phone number is valid, false otherwise.
 * @throws {InvalidPhoneNumberError} - Throws an InvalidPhoneNumberError if the phone number is invalid.
 * @example
 * const isValid = isValidPhoneNumber('+14155552671', 'US');
 * console.log(isValid); // true
 */
export function isValidPhoneNumber(
  phone: string,
  countryCode: string
): boolean {
  try {
    const phoneNumber = phoneUtil.parseAndKeepRawInput(phone, countryCode);
    const isValid = phoneUtil.isValidNumber(phoneNumber);
    if (!isValid) {
      throw new InvalidPhoneNumberError(`Invalid phone number: ${phone}`);
    }
    return isValid;
  } catch (error) {
    throw new InvalidPhoneNumberError(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
}

/**
 * Formats a phone number in E.164 format.
 * @param {string} phone - The phone number to format.
 * @param {string} countryCode - The ISO 3166-1 two-letter country code.
 * @returns {string} - Returns the phone number in E.164 format.
 * @throws {PhoneNumberFormatError} - Throws a PhoneNumberFormatError if the phone number can't be formatted.
 * @example
 * const formattedPhone = formatPhoneNumberE164('+14155552671', 'US');
 * console.log(formattedPhone); // '+14155552671'
 */
export function formatPhoneNumberE164(
  phone: string,
  countryCode: string
): string {
  try {
    const phoneNumber = phoneUtil.parseAndKeepRawInput(phone, countryCode);
    if (!phoneUtil.isValidNumber(phoneNumber)) {
      throw new InvalidPhoneNumberError(`Invalid phone number: ${phone}`);
    }
    return phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
  } catch (error) {
    throw new PhoneNumberFormatError(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
}
